import type { SelectedProductGroup } from "@/features/Generations/context/PilotStateContext";
import type { BlueprintMessage } from "@/types/creativesphere";

import { buildSeedRows } from "@/features/Generations/utils/acceptPilotSeedRows";
import { CloseIcon, ImageOutlinedIcon } from "@/icons";
import { MuiBox } from "@/orion/MuiComponents/MuiBox";
import { MuiButton } from "@/orion/MuiComponents/MuiButton";
import { MuiChip } from "@/orion/MuiComponents/MuiChip";
import { MuiDialog } from "@/orion/MuiComponents/MuiDialog";
import { MuiDialogActions } from "@/orion/MuiComponents/MuiDialogActions";
import { MuiDialogContent } from "@/orion/MuiComponents/MuiDialogContent";
import { MuiDialogTitle } from "@/orion/MuiComponents/MuiDialogTitle";
import { MuiIconButton } from "@/orion/MuiComponents/MuiIconButton";
import { MuiTypography } from "@/orion/MuiComponents/MuiTypography";
import { PRODUCT_GROUP_TYPE } from "@/types/propagation";

function groupTypeLabel(groupType: string | undefined): string {
  switch (groupType) {
    case PRODUCT_GROUP_TYPE.TAG:
      return "Tag";
    case PRODUCT_GROUP_TYPE.CATEGORY:
      return "Category";
    // An All Products launch is typed UNSPECIFIED — it names no taxonomy.
    case PRODUCT_GROUP_TYPE.UNSPECIFIED:
      return "All products";
    case PRODUCT_GROUP_TYPE.COLLECTION:
    default:
      return "Collection";
  }
}

export interface AcceptPilotModalProps {
  open: boolean;
  onClose: () => void;
  /** Confirm acceptance and propagate the pilot across the collection. */
  onPropagate: () => void;
  /**
   * Image of the accepted pilot shown on the left. For now this is the selected
   * product image used as a placeholder — it will be replaced with the rendered
   * pilot image once generation is wired up in a later ticket.
   */
  pilotImageSrc?: string;
  pilotImageAlt?: string;
  /**
   * The blueprint the pilot was built from. Its seeds_map fills in the Scene
   * and Photography rows of the seed summary.
   */
  blueprint?: BlueprintMessage;
  /** The selected pilot product, shown on the Product row. */
  productName?: string;
  productGroup?: SelectedProductGroup;
  collectionCount: number;
  busy?: boolean;
}

/**
 * "Accept the pilot" modal — opened from the Propagate button on the setup
 * canvas. Summarizes the seed inputs the pilot was made from (which hold and
 * which swap per SKU) before the user propagates it across the collection.
 * The seed summary and collection count are placeholder data for now.
 */
export function AcceptPilotModal({
  open,
  onClose,
  onPropagate,
  pilotImageSrc,
  pilotImageAlt,
  blueprint,
  productName,
  productGroup,
  collectionCount,
  busy = false,
}: AcceptPilotModalProps) {
  const seedRows = buildSeedRows(blueprint, productName);
  return (
    <MuiDialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      sx={{ "& .MuiDialog-paper": { maxWidth: 880 } }}
    >
      <MuiDialogTitle>
        <MuiBox
          sx={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            gap: 2,
          }}
        >
          <MuiTypography variant="h5" component="span" fontWeight={700}>
            Accept the pilot
          </MuiTypography>
          <MuiIconButton
            aria-label="Close"
            size="small"
            onClick={onClose}
            sx={{ flexShrink: 0 }}
          >
            <CloseIcon fontSize="small" />
          </MuiIconButton>
        </MuiBox>
      </MuiDialogTitle>

      <MuiDialogContent>
        {productGroup && (
          <MuiBox
            data-testid="accept-pilot-group"
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 2,
              mb: 4,
              px: 4,
              py: 3,
              borderRadius: 2,
              bgcolor: "action.hover",
            }}
          >
            <MuiChip
              label={groupTypeLabel(productGroup.group_type)}
              size="small"
              color="primary"
              variant="outlined"
            />
            {(productGroup.handle || productGroup.group_id) && (
              <MuiTypography variant="body2" fontWeight={700}>
                {productGroup.handle || productGroup.group_id}
              </MuiTypography>
            )}
          </MuiBox>
        )}

        <MuiTypography variant="body1" color="text.secondary" sx={{ mb: 5 }}>
          Two acceptances, not one. The product swaps out for each of the other{" "}
          {collectionCount - 1} — everything else the generation is made of
          holds still. You will review the collection itself before anything is
          released.
        </MuiTypography>

        <MuiBox
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "180px minmax(0, 1fr)" },
            gap: 5,
            alignItems: "start",
          }}
        >
          {/* Accepted pilot image — currently the selected product image as a
              placeholder for the yet-to-be-rendered pilot image. */}
          <MuiBox sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <MuiBox
              sx={{
                aspectRatio: "1 / 1",
                borderRadius: 2,
                overflow: "hidden",
                bgcolor: "action.hover",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "text.disabled",
              }}
            >
              {pilotImageSrc ? (
                <MuiBox
                  component="img"
                  src={pilotImageSrc}
                  alt={pilotImageAlt ?? "Accepted pilot"}
                  sx={{ width: "100%", height: "100%", objectFit: "cover" }}
                />
              ) : (
                <ImageOutlinedIcon sx={{ fontSize: 48 }} />
              )}
            </MuiBox>
            <MuiTypography
              variant="overline"
              color="text.secondary"
              sx={{ letterSpacing: 0.6 }}
            >
              Accepted pilot
            </MuiTypography>
          </MuiBox>

          <MuiBox sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            {seedRows.map((row) => (
              <MuiBox
                key={row.label}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 3,
                  px: 4,
                  py: 3,
                  borderRadius: 2,
                  bgcolor: "action.hover",
                }}
              >
                <MuiTypography
                  variant="overline"
                  color="text.secondary"
                  sx={{ letterSpacing: 0.6, flexShrink: 0, width: 120 }}
                >
                  {row.label}
                </MuiTypography>
                <MuiTypography
                  variant="body2"
                  fontWeight={700}
                  sx={{ flex: 1 }}
                >
                  {row.value}
                </MuiTypography>
                <MuiChip
                  label={row.disposition === "changes" ? "Changes" : "Locked"}
                  size="small"
                  variant="outlined"
                  color={row.disposition === "changes" ? "primary" : "success"}
                  sx={{ flexShrink: 0 }}
                />
              </MuiBox>
            ))}
          </MuiBox>
        </MuiBox>

        <MuiTypography variant="body2" color="text.secondary" sx={{ mt: 5 }}>
          Copy is not copied across. Each product re-resolves the same attribute
          field through the same brand voice, which is why {collectionCount}{" "}
          product pages do not end up saying the same sentence.
        </MuiTypography>
      </MuiDialogContent>

      <MuiDialogActions sx={{ px: 6, py: 4, gap: 2 }}>
        <MuiButton onClick={onClose}>Not yet</MuiButton>
        <MuiButton variant="contained" onClick={onPropagate} disabled={busy}>
          Propagate to all {collectionCount}
        </MuiButton>
      </MuiDialogActions>
    </MuiDialog>
  );
}
